import { memo } from 'react';
import { token } from '@atlaskit/tokens';
import { Icon, IconType } from '@components/shared/Icon';
import { css } from '@emotion/react';
import { CardStatus } from '@components/learning/LearningSidebar/constants';
import { Appearance, TextTooltip } from './TextTooltip';
import { SectionItem as SectionItemType } from '@components/learning/LearningSidebar/types';

export interface SectionProps {
  section: SectionItemType;
  status: CardStatus;
  courseSlug?: string;
  lessonSlug?: string;
  isSelected: boolean;
  onSelect: (courseSlug: string, lessonSlug?: string, sectionSlug?: string) => void;
}

export const dotStateMap: Record<string, IconType> = {
  [CardStatus.DEFAULT]: 'dot-default',
  [CardStatus.COMPLETED]: 'dot-completed',
};

export const cleanupObserver = (sectionContentfulId: string, seenSectionObserver: IntersectionObserver): void => {
  const element = document.getElementById(`${sectionContentfulId}`);

  if (element) {
    seenSectionObserver.unobserve(element);
  }
};

export const SectionItem: React.FC<SectionProps> = memo(
  ({ section: { title, id }, status, courseSlug, lessonSlug, onSelect, isSelected }) => {
    const handleSectionClick = (e: React.MouseEvent): void => {
      e.stopPropagation();
      onSelect(courseSlug || '', lessonSlug, id);
    };

    const getIconType = (): string => {
      if (isSelected && status === CardStatus.COMPLETED) {
        return dotStateMap[status];
      }

      if (isSelected) {
        return 'dot-selected';
      }

      return dotStateMap[status];
    };

    const type = getIconType();

    return (
      <div
        data-testid="sidebar-section-item"
        css={[sectionBarStyles, isSelected && sectionBarSelectedStyles]}
        onClick={handleSectionClick}
      >
        <Icon type={type as IconType} color={token('color.icon')} />
        <TextTooltip text={title} status={status} isSelected={isSelected} appearance={Appearance.SECTION} />
      </div>
    );
  },
);

const sectionBarStyles = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '4px 0 4px 10px',
  borderRadius: '4px',
  cursor: 'pointer',

  '&:active': {
    background: `${token('color.background.accent.gray.subtlest.hovered')} !important`,
  },

  '&:hover': {
    background: 'rgba(220, 223, 228, 0.50)',

    '&::before': {
      content: '""',
      display: 'block',
      width: '2px',
      height: '12px',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      background: token('color.background.neutral'),
    },
  },
});

const sectionBarSelectedStyles = css({
  '&::before': {
    content: '""',
    display: 'block',
    width: '2px',
    height: '12px',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    background: `${token('color.text.selected')} !important`,
  },
});
